import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function OrderSuccessPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { api } = useAuth();
  const { clearCart } = useCart();
  const [status, setStatus] = useState('loading');
  const [paymentData, setPaymentData] = useState(null);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      checkPaymentStatus();
    } else {
      setStatus('error');
    }
  }, [sessionId]);

  const checkPaymentStatus = async (attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    try {
      const response = await api.get(`/payments/status/${sessionId}`);
      setPaymentData(response.data);

      if (response.data.payment_status === 'paid') {
        setStatus('success');
        clearCart();
      } else if (response.data.status === 'expired') {
        setStatus('expired');
      } else if (attempts < maxAttempts) {
        setTimeout(() => checkPaymentStatus(attempts + 1), pollInterval);
      } else {
        setStatus('pending');
      }
    } catch (error) {
      console.error('Payment status check failed:', error);
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 pt-24 pb-12 flex items-center justify-center">
      <div className="max-w-md w-full mx-4">
        <div className="bg-white rounded-3xl shadow-xl p-8 text-center">
          {status === 'loading' && (
            <>
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-orange-100 flex items-center justify-center">
                <Loader2 className="w-10 h-10 text-orange-500 animate-spin" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900 mb-2">Processing Payment</h1>
              <p className="text-slate-500">Please wait while we confirm your payment...</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle className="w-10 h-10 text-green-500" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900 mb-2">Order Confirmed!</h1>
              <p className="text-slate-500 mb-6">
                Your payment was successful. We've sent a confirmation to your email.
              </p>
              {paymentData && (
                <div className="bg-slate-50 rounded-lg p-4 mb-6">
                  <p className="text-sm text-slate-500">Amount Paid</p>
                  <p className="text-2xl font-bold text-slate-900">
                    £{(paymentData.amount / 100).toFixed(2)}
                  </p>
                </div>
              )}
              <div className="space-y-3">
                <Button 
                  className="btn-primary w-full"
                  onClick={() => navigate('/orders')}
                  data-testid="view-orders-button"
                >
                  View My Orders
                </Button>
                <Button 
                  variant="outline"
                  className="w-full"
                  onClick={() => navigate('/')}
                >
                  Back to Home
                </Button>
              </div>
            </>
          )}

          {status === 'expired' && (
            <>
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-100 flex items-center justify-center">
                <XCircle className="w-10 h-10 text-red-500" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900 mb-2">Session Expired</h1>
              <p className="text-slate-500 mb-6">
                Your payment session has expired. Please try again.
              </p>
              <Button 
                className="btn-primary w-full"
                onClick={() => navigate('/cart')}
              >
                Return to Cart
              </Button>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-100 flex items-center justify-center">
                <XCircle className="w-10 h-10 text-red-500" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900 mb-2">Something Went Wrong</h1>
              <p className="text-slate-500 mb-6">
                We couldn't verify your payment. If you were charged, please contact support.
              </p>
              <div className="space-y-3">
                <Button 
                  className="btn-primary w-full"
                  onClick={() => navigate('/cart')}
                >
                  Return to Cart
                </Button>
                <Button 
                  variant="outline"
                  className="w-full"
                  onClick={() => navigate('/contact')}
                >
                  Contact Support
                </Button>
              </div>
            </>
          )}

          {status === 'pending' && (
            <>
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-yellow-100 flex items-center justify-center">
                <Loader2 className="w-10 h-10 text-yellow-500" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900 mb-2">Payment Processing</h1>
              <p className="text-slate-500 mb-6">
                Your payment is still being processed. Check your orders page for updates.
              </p>
              <Button 
                className="btn-primary w-full"
                onClick={() => navigate('/orders')}
              >
                View Orders
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
